unit uButtonPanel;

{
   UPDATED: 4/20/26 -- added support for TTMSFNCPanel with TWebPanels on it

   This class takes a Panel of some kind that has "fake" buttons on it that are TWebPanels
   and manage them as independent button groups.

   The main purpose is to get more flexibility controlling Pages in TWebPageControls than the
   tabs offer. Also, there's a bug that tends to hide the tabs when the component is Client-aligned.
   You can give it a kick by changing to another page, then they'll appear, but that's a lousy solution.
   Also, reglar TWebButtons have some odd limitations that Panels don't, like if you change the color,
   it's not visible at design-time, only run-time.

   For panels used as buttons, it's similar to what you might get by adding a GroupNum property to
   some buttons like the old TBitBtns. For labels, there's nothing comparable.

   There's an abstract base class, TButtonPanel, and two derived classes based on what's being
   used as a "button: TWebPanel or TWebLabel

   bp1 : TPnlButtonPanel;
   pnl_button1, pnl_button2, pnl_button3 : TWebPanel;
   bp2 : TLblButtonPanel;
   lbl_button1, lbl_button2, lbl_button3 : TWebLabel;
   . . .
   bp1 := TPnlButtonPanel.Create( panel1, [pnl_button1, pnl_button2, pnl_button3], bp1_onclick_handler );
   bp2: = TLblButtonPanel.Create( panel2, [lbl_button1, lbl_button2, lbl_button3], bp2_onclick_handler );

   NOTE: it should work with any TControl as a base, not just panels. I'm using a TWebGridPanel in one case
}
interface

uses
  System.SysUtils, System.Classes, System.StrUtils,
  WEBLib.Graphics, WEBLib.Controls, WEBLib.StdCtrls,
  WEBLib.Forms, WEBLib.Dialogs, WEBLib.ComCtrls, WEBLib.ExtCtrls,
  VCL.TMSFNCTypes, VCL.TMSFNCUtils, VCL.TMSFNCGraphics,
  VCL.TMSFNCGraphicsTypes, Vcl.Controls, Vcl.StdCtrls; //, uCommonTypes;

const
  save_edit_btn_unselected_color : TColor = clBtnFace;
  save_edit_btn_selected_color   : TColor = clPalegreen;   // ignore warning
  save_edit_btn_highlight_color  : TColor = clwhite;


type
  TPanelArray = array of TPanel;
  TLabelArray = array of TLabel;

  TButtonPanel = class
  private
    FBasePanel     : TControl;
    FDoOnClick     : TNotifyEvent;
    FDoOnHelpClick : TNotifyEvent;
    FPageControl   : TPageControl;
    Fbtn_unselected_color : TColor;
    Fbtn_selected_color   : TColor;
    Fbtn_highlight_color  : TColor;
    FSetFontBoldOnSelection : Boolean;

    procedure DoMouseEnter(Sender: TObject); virtual; abstract;
    procedure DoMouseLeave(Sender: TObject); virtual; abstract;
    procedure DoClick(Sender: TObject); virtual; abstract;
    procedure DoHelpClick(Sender: TObject); virtual; abstract;

    procedure Set_FDoOnClick(const Value: TNotifyEvent);
    procedure Set_FDoOnHelpClick(const Value: TNotifyEvent);
    procedure redirect_to_local_events; virtual; abstract;
    function  Get_TabIndex: integer; virtual; abstract;
    procedure Set_TabIndex(const Value: integer); virtual; abstract;

  protected
    function  FindButton(Sender: TObject): TPanel; virtual;

    constructor Create( aBasePanel : TControl );

    property BasePanel      : TControl {TWebPanel} read FBasePanel;
    property DoOnClick      : TNotifyEvent read FDoOnClick write Set_FDoOnClick;
    property DoOnHelpClick  : TNotifyEvent read FDoOnHelpClick write Set_FDoOnHelpClick;

    // use TabIndex just to set a specific page and set the selected button as well
    property TabIndex : integer read Get_TabIndex write Set_TabIndex;

  public
    // use PageControl ONLY for setting properties that don't change the page!
    property PageControl  : TPageControl read FPageControl write FPageControl;

    property btn_unselected_color   : TColor  read Fbtn_unselected_color write Fbtn_unselected_color;
    property btn_selected_color     : TColor  read Fbtn_selected_color   write Fbtn_selected_color;
    property btn_highlight_color    : TColor  read Fbtn_highlight_color  write Fbtn_highlight_color;
    property SetFontBoldOnSelection : Boolean read FSetFontBoldOnSelection write FSetFontBoldOnSelection;

    procedure ClearSelection; virtual; abstract;

    // ShowPageControlPage sets to the Selected btn
    procedure ShowPageControlPage; virtual; abstract;
  end;
  //****************************************************

  TPnlButtonPanel = class( TButtonPanel )
  private
    FPnlButtons       : TPanelArray;
    FPrevSelected_btn : TPanel;
    FSelected_btn     : TPanel;

    procedure DoMouseEnter(Sender: TObject); override;
    procedure DoMouseLeave(Sender: TObject); override;
    procedure DoClick(Sender: TObject);
    procedure DoHelpClick(Sender: TObject);
    procedure redirect_to_local_events; override;
    function  Get_TabIndex: integer; override;
    procedure Set_TabIndex(const Value: integer); override;

  public
    constructor Create( aBasePanel : TControl {TWebPanel}; aButtons : TPanelArray );

    property PnlButtons   : TPanelArray read FPnlButtons;
    property PrevSelected_btn : TPanel read FPrevSelected_btn;
    property Selected_btn : TPanel read FSelected_btn;

    property BasePanel;
    property DoOnClick;
    property DoOnHelpClick;
    property TabIndex;

    procedure AddButton( aBtn : TPanel );

    procedure ClearSelection; override;

    procedure ShowPageControlPage; override;
  end;
  //****************************************************

  TDoClickProc = reference to procedure ( aSender : TLabel );

  TLblButtonPanel = class( TButtonPanel )
  private
    FLblButtons : TLabelArray;
    FSelected_btn : TLabel;
    FDoClickProc : TDoClickProc;

    function  FindButton(Sender: TObject): TLabel;
    procedure DoMouseEnter(Sender: TObject); override;
    procedure DoMouseLeave(Sender: TObject); override;
    procedure DoClick(Sender: TObject);
    procedure redirect_to_local_events; override;
    function  Get_TabIndex: integer; override;
    procedure Set_TabIndex(const Value: integer); override;

  public
    constructor Create( aBasePanel : TPanel; aButtons : TLabelArray );

    property LblButtons   : TLabelArray read FLblButtons;
    property Selected_btn : TLabel read FSelected_btn;

    // call this in DoClickProc to get the buttons to update
    procedure SelectButtonLabel(Sender: TObject);
    property  DoClickProc  : TDoClickProc read FDoClickProc write FDoClickProc;

    property BasePanel;
    property DoOnClick;
    property TabIndex;

    procedure ClearSelection; override;

    procedure ShowPageControlPage; override;

  end;

implementation

{ TButtonPanel }

//===============================================================
constructor TButtonPanel.Create( aBasePanel: TControl {TWebPanel} );
//===============================================================
// NOTE: the compiler refuses to accept sending in the DoOnClick parameter here,
// so it needs to be assigned via the property's Setter separately
begin
  FBasePanel := aBasePanel;
  Fbtn_unselected_color   := save_edit_btn_unselected_color;
  Fbtn_selected_color     := save_edit_btn_selected_color;
  Fbtn_highlight_color    := save_edit_btn_highlight_color;
  FSetFontBoldOnSelection := False;

end;

//===============================================================
procedure TButtonPanel.Set_FDoOnClick(const Value: TNotifyEvent);
//===============================================================
begin
  FDoOnClick := Value;
  redirect_to_local_events;
end;

//===============================================================
procedure TButtonPanel.Set_FDoOnHelpClick(const Value: TNotifyEvent);
//===============================================================
begin
  FDoOnHelpClick := Value;
end;

//===============================================================
function TButtonPanel.FindButton(Sender: TObject) : TPanel;
//===============================================================
// --> NIL if not found
begin
  Result := NIL;
  if (Sender is TPanel) then
    Result := TPanel(Sender)
  else if (Sender is TLabel) and (TLabel(Sender).Parent is TPanel) then
    Result := TPanel(TLabel(Sender).Parent);
end;

//===============================================================
                          { TPnlButtonPanel }
//===============================================================

//===============================================================
constructor TPnlButtonPanel.Create(aBasePanel: TControl {TWebPanel}; aButtons: TPanelArray);
//===============================================================
// NOTE: the compiler refuses to accept sending in the DoOnClick parameter here, so it needs to be
// assigned via the property's Setter separately
begin
  inherited Create( aBasePanel );

  FPnlButtons       := aButtons;
  FPrevSelected_btn := NIL;
  FSelected_btn     := NIL;
  redirect_to_local_events;
end;

//===============================================================
function TPnlButtonPanel.Get_TabIndex: integer;
//===============================================================
begin
  Result := PageControl.TabIndex;
end;

//===============================================================
procedure TPnlButtonPanel.Set_TabIndex(const Value: integer);
//===============================================================
var
  pnl : TPanel;
begin
  PageControl.TabIndex := Value;
  pnl := FPnlButtons[Value];
  DoClick( pnl );
end;

//===============================================================
procedure TPnlButtonPanel.redirect_to_local_events;
//===============================================================
// This redirects OnClick for all of the buttons to OUR click handler
var
  n : integer;
  pnl : TPanel;
  lbl : TLabel;

begin
  for n := 0 to Length(FPnlButtons)-1 do
  begin
    pnl := FPnlButtons[n];
    pnl.OnClick := DoClick;

    // if there's a label on the panel, then point it's OnClick here as well
    if (pnl.ControlCount > 0) and (pnl.Controls[0] is TLabel) then
      TLabel(pnl.Controls[0]).OnClick := DoClick;

    pnl.OnMouseEnter := DoMouseEnter;
    pnl.OnMouseLeave := DoMouseLeave;
  end;
end;

//===============================================================
procedure TPnlButtonPanel.DoMouseEnter(Sender: TObject);
//===============================================================
var
  pnl : TPanel;
begin
  pnl := FindButton(Sender);
  if assigned(pnl) then
  begin
    pnl.Color := Fbtn_highlight_color;
//    if FSetFontBoldOnSelection then    This doesn't work but Bruno says it does
//    begin
      //FSelected_btn.Font.Style := FSelected_btn.Font.Style + [fsBold];
      //FSelected_btn.Caption := '<b>'+FSelected_btn.Caption+'</b>';
      //FSelected_btn.Caption := 'xyz';
//    end;
  end;
end;

//===============================================================
procedure TPnlButtonPanel.DoMouseLeave(Sender: TObject);
//===============================================================
var
  pnl : TPanel;
begin
  pnl := FindButton(Sender);
  if assigned(pnl) then
  begin
    if (pnl = Selected_btn) then
    begin
      pnl.Color := Fbtn_selected_color;
//      if FSetFontBoldOnSelection then
//        FSelected_btn.Font.Style := FSelected_btn.Font.Style + [fsBold];
    end
    else
    begin
      pnl.Color := Fbtn_unselected_color;
//      if FSetFontBoldOnSelection then
//        FSelected_btn.Font.Style := FSelected_btn.Font.Style - [fsBold];
    end;
  end;
end;

//===============================================================
procedure TPnlButtonPanel.DoClick(Sender: TObject);
//===============================================================
var
  pnl : TPanel;
begin
  pnl := FindButton(Sender);
  if assigned(pnl) then
  begin
    if Assigned(Selected_btn) and (pnl <> Selected_btn) then
      FSelected_btn.Color := Fbtn_unselected_color;

    FPrevSelected_btn := FSelected_btn;
    FSelected_btn := pnl;
    FSelected_btn.Color := Fbtn_selected_color;
//    if FSetFontBoldOnSelection then
//      FSelected_btn.Font.Style := FSelected_btn.Font.Style + [fsBold];
  end;

  if Assigned( FDoOnClick ) then
    FDoOnClick( pnl );
end;

//===============================================================
procedure TPnlButtonPanel.DoHelpClick(Sender: TObject);
//===============================================================
begin
  if (Sender is TImageControl) and Assigned( FDoOnHelpClick ) then
    FDoOnHelpClick( Sender );
end;

//===============================================================
procedure TPnlButtonPanel.AddButton(aBtn: TPanel);
//===============================================================
begin
  SetLength( FPnlButtons, Length(FPnlButtons) + 1 );
  FPnlButtons[High(FPnlButtons)] := aBtn;
end;

//===============================================================
procedure TPnlButtonPanel.ClearSelection;
//===============================================================
begin
  if Assigned(FSelected_btn) then
  begin
    FSelected_btn.Color := Fbtn_unselected_color;
    FSelected_btn := NIL;
  end;
end;

//===============================================================
procedure TPnlButtonPanel.ShowPageControlPage;
//===============================================================
begin
  if Assigned( PageControl ) then
    PageControl.TabIndex := Selected_btn.Tag
end;

//===============================================================
                        { TLblButtonPanel }
//===============================================================

//===============================================================
constructor TLblButtonPanel.Create(aBasePanel: TPanel; aButtons: TLabelArray);
//===============================================================
begin
  inherited Create( aBasePanel );

  FBasePanel := aBasePanel;
  FlblButtons   := aButtons;

  redirect_to_local_events;
end;

//===============================================================
function TLblButtonPanel.Get_TabIndex: integer;
//===============================================================
begin
  Result := PageControl.TabIndex;
end;

//===============================================================
procedure TLblButtonPanel.Set_TabIndex(const Value: integer);
//===============================================================
var
  lbl : TLabel;
begin
  PageControl.TabIndex := Value;
  lbl := FLblButtons[Value];
  DoClick( lbl );
end;

//===============================================================
procedure TLblButtonPanel.redirect_to_local_events;
//===============================================================
// point OnClick for all of the buttons to OUR click handler
var
  n : integer;
  lbl : TLabel;

begin
  for n := 0 to Length(FLblButtons)-1 do
  begin
    lbl := FLblButtons[n];
    lbl.OnClick := DoClick;

    lbl.OnMouseEnter := DoMouseEnter;
    lbl.OnMouseLeave := DoMouseLeave;
  end;
end;

//===============================================================
function TLblButtonPanel.FindButton(Sender: TObject) : TLabel;
//===============================================================
// --> NIL if not found
begin
  Result := NIL;
  if (Sender is TLabel) then
    Result := TLabel(Sender);
end;

//===============================================================
procedure TLblButtonPanel.DoMouseEnter(Sender: TObject);
//===============================================================
var
  lbl : TLabel;
begin
  lbl := FindButton(Sender);
  if assigned(lbl) then
  begin
    lbl.Font.Style := [fsBold,fsUnderline];   // ignore this error
  end;
end;

//===============================================================
procedure TLblButtonPanel.DoMouseLeave(Sender: TObject);
//===============================================================
var
  lbl : TLabel;
begin
  lbl := FindButton(Sender);
  if assigned(lbl) then
  begin
    if (lbl = Selected_btn) then
      lbl.Font.Style := [fsBold,fsUnderline]     // ignore this error
    else
      lbl.Font.Style := [fsUnderline];           // ignore this error
  end;
end;

//===============================================================
procedure TLblButtonPanel.SelectButtonLabel(Sender: TObject);
//===============================================================
var
  lbl : TLabel;
begin
  lbl := FindButton(Sender);
  if not assigned(lbl) then Exit;

  if Assigned(Selected_btn) and (lbl <> Selected_btn) then
    Selected_btn.Font.Style := [fsUnderline];           // ignore this error

  FSelected_btn := lbl;
  FSelected_btn.Font.Style := [fsBold,fsUnderline];     // ignore this error
end;

//===============================================================
procedure TLblButtonPanel.DoClick(Sender: TObject);
//===============================================================
var
  lbl : TLabel;
begin
  lbl := FindButton(Sender);
  if not assigned(lbl) then Exit;

  SelectButtonLabel( lbl );

  // now fill up the list of Lockers for this user
  //GetGoing( LockerIDs_cbo.Items );
  if Assigned( FDoOnClick ) then
    FDoOnClick( lbl )
  else if Assigned( FDoClickProc ) then
    FDoClickProc( lbl );
end;

//===============================================================
procedure TLblButtonPanel.ClearSelection;
//===============================================================
begin
  if Assigned(FSelected_btn) then
  begin
    FSelected_btn.Font.Style := [fsUnderline];   // remove Bold        // ignore this error
    FSelected_btn := NIL;
  end;
end;

//===============================================================
procedure TLblButtonPanel.ShowPageControlPage;
//===============================================================
begin
  if Assigned( PageControl ) then
    PageControl.TabIndex := Selected_btn.Tag
end;


end.
